/**
 * `/marketplace` sur mobile : en descendant dans la liste, l'en-tête du site sort de l'écran et la barre de
 * filtres remonte à sa place, les deux ensemble (même translation, même durée : `_compact-layout.scss`,
 * classe `mp-scroll-down`), et « Sauvegarder la recherche » se réduit à sa cloche. Un geste vers le haut
 * fait tout revenir.
 *
 * Seuils (Nielsen Norman Group) : il faut descendre de quelques pixels pour masquer et remonter un peu
 * plus pour réafficher, sinon l'en-tête clignote quand on relit une annonce.
 */

const HIDDEN_CLASS = "mp-scroll-down";
/** La liste passe sous la barre collée : fondu blanc sous la barre (`_bar-e3.scss`, rythme C). */
const STUCK_CLASS = "mp-bar-stuck";
/** Fin du glissé : la partie transparente de la barre laisse passer les appuis vers les cartes (`_bar-e3.scss`). */
const PASS_THROUGH_CLASS = "mp-bar-pass-through";
/** Durée du glissé de l'en-tête et de la barre (`$chrome-slide`, `_compact-layout.scss`). */
const SLIDE_MS = 200;
const HEADER_HEIGHT_VAR = "--mp-header-height";
const MOBILE_QUERY = "(max-width: 959.98px)";
const HIDE_AFTER_PX = 8;
const SHOW_AFTER_PX = 24;

export interface ScrollChromeState {
    hidden: boolean;
    /** Distance parcourue dans le sens courant depuis le dernier changement de sens. */
    travel: number;
}

/**
 * Décide si l'en-tête doit être masqué. `hideFrom` = position à partir de laquelle la barre de filtres est
 * collée en haut (avant, on voit encore le titre : rien à masquer).
 */
export function nextScrollChromeState(
    state: ScrollChromeState,
    previousY: number,
    currentY: number,
    hideFrom: number,
): ScrollChromeState {
    if (currentY <= hideFrom) {
        return { hidden: false, travel: 0 };
    }

    const delta = currentY - previousY;
    if (delta === 0) return state;

    const sameDirection = (delta > 0) === (state.travel >= 0);
    const travel = sameDirection ? state.travel + delta : delta;

    if (travel >= HIDE_AFTER_PX) return { hidden: true, travel };
    if (travel <= -SHOW_AFTER_PX) return { hidden: false, travel };

    return { hidden: state.hidden, travel };
}

/**
 * Position de défilement ramenée entre le haut et le bas de la page. Safari laisse dépasser la page en haut et
 * en bas (rebond élastique) : au pied de page, le retour du rebond fait redescendre `scrollY` sans que
 * l'utilisateur ait remonté, et ce faux geste vers le haut faisait revenir l'en-tête.
 */
export function clampScrollY(scrollY: number, maxScrollY: number): number {
    return Math.min(Math.max(scrollY, 0), Math.max(maxScrollY, 0));
}

/**
 * Le bas de page a bougé alors que la position est bornée (barre d'outils de Safari qui se replie, page qui
 * grandit) : la position bornée change sans que l'utilisateur ait défilé. Ce n'est pas un geste.
 */
export function isBoundaryShift(previousY: number, previousMax: number, scrollY: number, maxScrollY: number): boolean {
    return maxScrollY !== previousMax && (scrollY > maxScrollY || previousY > maxScrollY);
}

function maxScrollY(): number {
    const page = document.scrollingElement ?? document.documentElement;
    return page.scrollHeight - window.innerHeight;
}

/** Un panneau ou un menu est ouvert, ou la recherche du site a le focus : on ne masque rien. */
function isChromeLocked(header: HTMLElement, bar: HTMLElement): boolean {
    return (
        document.body.classList.contains("app-filter-open") ||
        document.body.style.overflow === "hidden" ||
        document.documentElement.style.overflow === "hidden" ||
        header.contains(document.activeElement) ||
        // Champ de recherche de la barre (e3) : la barre ne bouge pas sous ses suggestions (une pastille ou un
        // bouton qui garde le focus ne bloque rien)
        (document.activeElement instanceof HTMLInputElement && bar.contains(document.activeElement)) ||
        document.querySelector('[data-facet-dropdown-trigger][aria-expanded="true"]') !== null
    );
}

let initialized = false;

export function initMobileScrollChrome(): void {
    if (initialized || typeof window === "undefined") return;

    const header = document.getElementById("app-header");
    const chrome = document.querySelector<HTMLElement>(".marketplace-global-chrome");
    const bar = document.querySelector<HTMLElement>(".marketplace-global-chrome__action-bar");
    if (!header || !chrome || !bar) return;
    initialized = true;

    const root = document.documentElement;
    const mobile = window.matchMedia(MOBILE_QUERY);
    let state: ScrollChromeState = { hidden: false, travel: 0 };
    let lastMax = maxScrollY();
    let lastY = clampScrollY(window.scrollY, lastMax);
    let ticking = false;

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let passThroughTimer: number | undefined;

    const apply = (hidden: boolean): void => {
        if (hidden) {
            root.style.setProperty(HEADER_HEIGHT_VAR, `${header.offsetHeight}px`);
        }
        root.classList.toggle(HIDDEN_CLASS, hidden);

        window.clearTimeout(passThroughTimer);
        if (hidden) {
            passThroughTimer = window.setTimeout(
                () => root.classList.add(PASS_THROUGH_CLASS),
                reducedMotion.matches ? 0 : SLIDE_MS,
            );
        } else {
            root.classList.remove(PASS_THROUGH_CLASS);
        }
    };

    const show = (): void => {
        state = { hidden: false, travel: 0 };
        apply(false);
    };

    const update = (): void => {
        ticking = false;
        const max = maxScrollY();
        const currentY = clampScrollY(window.scrollY, max);
        const boundaryShift = isBoundaryShift(lastY, lastMax, window.scrollY, max);
        lastMax = max;
        // Bas du titre dans la page = point où la barre de filtres vient se coller sous l'en-tête
        const hideFrom = chrome.getBoundingClientRect().bottom + window.scrollY - header.offsetHeight;
        root.classList.toggle(STUCK_CLASS, mobile.matches && currentY > hideFrom);

        if (boundaryShift) {
            lastY = currentY;
            return;
        }

        if (!mobile.matches || isChromeLocked(header, bar)) {
            lastY = currentY;
            // Ex. le panneau Filtrer ramène la page en haut : l'en-tête doit y être en revenant
            if (state.hidden && (!mobile.matches || currentY <= hideFrom)) show();
            return;
        }

        const next = nextScrollChromeState(state, lastY, currentY, hideFrom);
        lastY = currentY;

        if (next.hidden !== state.hidden) apply(next.hidden);
        state = next;
    };

    window.addEventListener(
        "scroll",
        () => {
            if (ticking) return;
            ticking = true;
            requestAnimationFrame(update);
        },
        { passive: true },
    );

    // Passage ordinateur ↔ téléphone : en-tête affiché, fondu sous la barre recalculé
    mobile.addEventListener("change", () => {
        show();
        update();
    });

    // Page rouverte déjà défilée (retour arrière) : fondu sous la barre dès l'arrivée. Repris quand la page
    // et les polices sont chargées : la hauteur du titre, donc le point où la barre se colle, peut alors changer
    requestAnimationFrame(update);
    window.addEventListener("load", () => requestAnimationFrame(update), { once: true });
    document.fonts?.ready.then(() => requestAnimationFrame(update));

    // Navigation au clavier : un lien ou la recherche de l'en-tête prend le focus, l'en-tête revient
    header.addEventListener("focusin", () => {
        if (state.hidden) show();
    });

}
